import * as React from "react";
import { DrawerHeader } from "../../../../../components/DrawerHeader/DrawerHeader";
import { DrawerBody } from "../../../../../components/DrawerBody/DrawerBody";
import Drawer from "@mui/material/Drawer";
import { useMutation } from "react-query";
import {
  AiProcessingService,
  SystemSettings,
  SystemSettingsService,
  SystemSettingsUpdate
} from "../../../../../gen/clients";
import { Form, Formik, FormikProps } from "formik";
import Stack from "@mui/material/Stack";
import { TextInputField } from "../../../../../components/formikFields/TextInputField/TextInputField";
import { LoadingButton } from "../../../../../components/LoadingButton/LoadingButton";
import Button from "@mui/material/Button";
import { defaultInput, expectedOutput, matchingObjectStructure } from "../utils/matchingObject";
import { Accordion, AccordionDetails, AccordionSummary, Box, TextField, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";

interface Props {
  isAnalysis: boolean;
  systemSettings: SystemSettings;
  onSuccess: () => void;
  onClose: () => void;
}

enum FieldNames {
  instructions = "instructions",
  testData = "testData"
}

interface FormValues {
  [FieldNames.instructions]: string;
  [FieldNames.testData]: string;
}

const EditInstructionsPanel: React.FC<Props> = ({ isAnalysis, systemSettings, onClose, onSuccess }) => {
  const formikRef = React.useRef<FormikProps<FormValues>>(null);
  const [aiOutput, setAIOutput] = React.useState<string>("");
  const [validateWithDefaults, setValidateWithDefaults] = React.useState(true);
  const [isValidating, setIsValidating] = React.useState(false);

  const { mutateAsync: updateSystemSettings, error: saveError } = useMutation(
    SystemSettingsService.updateSystemSettings
  );

  const { mutateAsync: processTextByOpenAi } = useMutation(AiProcessingService.processTextByOpenAi);

  const onTest = React.useCallback(() => {
    setValidateWithDefaults(false);

    // Call validateForm asynchronously to make sure the state has updated.
    setTimeout(() => {
      formikRef.current?.validateForm();
      setValidateWithDefaults(true);
    }, 0);
  }, [setValidateWithDefaults]);

  const validate = React.useCallback(
    async formValues => {
      setIsValidating(true);
      const text = validateWithDefaults ? defaultInput(isAnalysis) : formValues[FieldNames.testData] || "";
      const errors: Record<string, string> = {};
      const response = await processTextByOpenAi({
        requestBody: {
          text,
          instructions: formValues[FieldNames.instructions] || ""
        }
      });

      if (!validateWithDefaults) {
        // fill ai output only for custom data testing
        setAIOutput(response.output || "");
      }

      try {
        const json = JSON.parse(response.output || "{}");
        const result = matchingObjectStructure(json, expectedOutput(isAnalysis));
        if (!result.match) {
          errors.instructions = `AI output doesn't match the expected structure.
        Try to modify Instructions to return correct output (missing keys: ${result.missingKeys})`;
        }
      } catch (_e) {
        errors.instructions = "The AI output has an invalid format. Please modify the instructions.";
      }
      setIsValidating(false);
      return errors;
    },
    [validateWithDefaults, setIsValidating, isAnalysis, processTextByOpenAi]
  );

  const onSubmit = React.useCallback(
    async values => {
      if (systemSettings?.emailFrom) {
        const updatedSettings: SystemSettingsUpdate = {
          ...systemSettings,
          emailProcessorSettings: {
            ...systemSettings.emailProcessorSettings,
            openAI: {
              ...systemSettings.emailProcessorSettings?.openAI,
              prompts: {
                ...systemSettings.emailProcessorSettings?.openAI?.prompts,
                [isAnalysis ? "emailAnalysis" : "emailScreening"]: values[FieldNames.instructions] || ""
              }
            }
          }
        };
        await updateSystemSettings({ requestBody: updatedSettings });
        onSuccess();
      }
    },
    [isAnalysis, onSuccess, systemSettings, updateSystemSettings]
  );

  const dialogTitle = React.useMemo(
    () => (isAnalysis ? "Edit Analysis Instructions" : "Edit Screening Instructions"),
    [isAnalysis]
  );

  const isSubmitting = React.useMemo(() => formikRef.current?.isSubmitting || false, []);
  const isTesting = React.useMemo(() => isValidating && !formikRef.current?.isSubmitting, [isValidating]);

  return (
    <Drawer open={true} onClose={onClose} anchor="right" PaperProps={{ sx: { width: "50vw" } }}>
      <DrawerHeader title={dialogTitle} onClose={onClose} />
      <DrawerBody>
        <Formik
          innerRef={formikRef}
          initialValues={{
            [FieldNames.instructions]:
              (isAnalysis
                ? systemSettings?.emailProcessorSettings?.openAI?.prompts.emailAnalysis
                : systemSettings?.emailProcessorSettings?.openAI?.prompts.emailScreening) || "",
            [FieldNames.testData]: ""
          }}
          validateOnMount={false}
          validateOnChange={false}
          validateOnBlur={false}
          validate={validate}
          onSubmit={onSubmit}
        >
          <Form noValidate={true}>
            <Stack spacing={3} mt={3}>
              <TextInputField
                name={FieldNames.instructions}
                label="Instructions"
                helperText="Instructions for analyzing job request emails"
                variant="outlined"
                multiline={true}
                rows={10}
              />
              <Box sx={{ pt: 2 }}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body1" fontWeight="bold">
                      Test Instructions
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={3} mt={3}>
                      <TextInputField
                        name={FieldNames.testData}
                        label="Sample text"
                        helperText="Enter a sample text of an email"
                        variant="outlined"
                        multiline={true}
                        rows={5}
                      />
                      <TextField
                        label="AI Output"
                        variant="outlined"
                        multiline={true}
                        rows={5}
                        aria-readonly
                        disabled
                        value={aiOutput}
                        fullWidth
                      />
                    </Stack>
                    <Stack direction="row" spacing={1} mt={3}>
                      <LoadingButton
                        color="secondary"
                        variant="outlined"
                        onClick={onTest}
                        type="button"
                        isLoading={isTesting}
                        disabled={isSubmitting}
                      >
                        Test
                      </LoadingButton>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </Box>
              {saveError && <SnackbarApiError error={saveError} />}
            </Stack>
            <Stack direction="row" spacing={1} mt={3}>
              <LoadingButton isLoading={isSubmitting} disabled={isTesting}>
                Save
              </LoadingButton>
              <Button color="secondary" onClick={onClose} disabled={isTesting || isSubmitting}>
                Cancel
              </Button>
            </Stack>
          </Form>
        </Formik>
      </DrawerBody>
    </Drawer>
  );
};

export { EditInstructionsPanel };
